import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Kicker } from "./motion";

// Reusable narrative chapter with a bled, parallax image.
export default function Chapter({
  id,
  number,
  title,
  problem,
  solution,
  image,
  imageAlt,
  imageSide = "right",
  outcome,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  const text = (
    <div className="md:col-span-5 flex flex-col justify-center py-16 md:py-32">
      <Reveal>
        <div className="flex items-baseline gap-4 mb-8">
          <span className="font-display text-brass text-lg">{number}</span>
          <Kicker>Chapter</Kicker>
        </div>
        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.1] text-verlo mb-10">
          {title}
        </h2>
        <p className="text-lg md:text-xl leading-relaxed text-verlo max-w-md mb-6">
          {problem}
        </p>
        <p className="text-lg md:text-xl leading-relaxed text-verlo max-w-md">
          {solution}
        </p>
        {outcome && (
          <p className="mt-10 font-display text-xl md:text-2xl text-brass max-w-sm leading-snug">
            {outcome}
          </p>
        )}
      </Reveal>
    </div>
  );

  const media = (
    <div className="md:col-span-7 relative overflow-hidden rounded-lg md:rounded-xl shadow-soft min-h-[56vh] md:min-h-[88vh] my-auto">
      <motion.img
        src={image}
        alt={imageAlt}
        style={{ y }}
        className="absolute inset-0 w-full h-[110%] object-cover -top-[5%]"
        loading="lazy"
      />
    </div>
  );

  return (
    <section
      id={id}
      data-testid={`chapter-${id}`}
      ref={ref}
      className="px-6 md:px-12 border-t border-verlo/10"
    >
      <div className="mx-auto max-w-[1400px] grid md:grid-cols-12 gap-8 md:gap-20 py-8 md:py-16">
        {imageSide === "left" ? (
          <>
            {media}
            {text}
          </>
        ) : (
          <>
            {text}
            {media}
          </>
        )}
      </div>
    </section>
  );
}
